/**
 * @file TonnageAdditionBadge.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-23
 * @modified 2026-10-23
 * @description The pill on an order sharing tonnage with the customer's other
 *              order of that day (utils/tonnageAdditions): "Includes 1.94 T
 *              from SO-D10421A" on the order invoicing it, "Added to
 *              SO-D10545" on the split order whose tonnage went. A click
 *              opens the other order when `onOpenOrder` is given, else the
 *              Added tonnage window (AddToOrderModal — Take it back out is
 *              there). Nothing renders for an order sharing none.
 */

import React, { useState } from 'react';
import AddToOrderModal from './AddToOrderModal';
import { includesPillText, addedToPillText } from '../utils/tonnageAdditions';
import { formatTonnes } from '../utils/numberFormatters';
import './TonnageAddition.css';

/**
 * @param {object}   additions  The order's `tonnage_additions` ({ added_to, includes }).
 * @param {number}   orderId
 * @param {function} [onChanged]   After the window saved something.
 * @param {function} [onOpenOrder] (orderId) — opens the other order instead of the window.
 * @param {string}   [variant]     'badge' (default), 'compact' ("→ SO-D10545" / "+1.94 T" — a
 *                                 search table's order cell) or 'icon'.
 */
function TonnageAdditionBadge({ additions, orderId, onChanged, onOpenOrder, variant = 'badge' }) {
  const [open, setOpen] = useState(false);
  const addedTo = (additions?.added_to || []).filter(Boolean);
  const includes = (additions?.includes || []).filter(Boolean);
  if (!orderId || (!addedTo.length && !includes.length)) return null;
  const words = addedTo.length ? addedToPillText(addedTo) : includesPillText(includes);
  const tonnes = includes.reduce((t, a) => t + (Number(a.tonnes) || 0), 0);
  const compact = addedTo.length ? `→ ${addedTo[0].target_order_number}` : `+${formatTonnes(tonnes)}`;
  const otherId = addedTo.length ? addedTo[0].target_order_id : (includes.length === 1 ? includes[0].source_order_id : null);
  const title = addedTo.length
    ? `${words} — this split order's tonnage is invoiced on ${addedTo[0].target_order_number}, not on its own.`
    : `${words} — invoiced on this order's line.`;

  const show = (e) => {
    e.stopPropagation();
    if (onOpenOrder && otherId) onOpenOrder(otherId);
    else setOpen(true);
  };
  return (
    <>
      <span
        className={`tonnage-badge${variant === 'icon' ? ' is-icon' : ''}`}
        title={`${title}\nClick ${onOpenOrder && otherId ? 'to open it' : 'for the added tonnage'}.`}
        role="button"
        tabIndex={0}
        data-read-safe
        onClick={show}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') show(e); }}
      >
        <span className="material-symbols-rounded">call_merge</span>
        {variant === 'compact' ? compact : variant !== 'icon' && words}
      </span>
      {open && (
        <span onClick={(e) => e.stopPropagation()}>
          <AddToOrderModal
            orderId={orderId}
            onClose={() => setOpen(false)}
            onDone={() => onChanged && onChanged()}
          />
        </span>
      )}
    </>
  );
}

export default TonnageAdditionBadge;
